import { type ReactNode } from 'react';
import { useTaskData, useTaskSearch, useTaskWrites } from './task-route';
import { TaskSurface, type TaskMode } from './task-surface';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A task opened OVER a list: the drawer and the modal.                      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **C8 BUILT ALL THREE FRAMES AND NOTHING EVER OPENED TWO OF THEM.** `task-surface.tsx` has
 * drawn the drawer, the modal and the full page since it landed, with every measurement taken off
 * `markup.html` — and the only caller was `/tasks/:key`, which hard-codes `full`. So a task always
 * opened as a page, the mode buttons on that page navigated away instead of shrinking, and the two
 * other frames were unreachable. Built, working and inert: the failure CLAUDE.md §4 names, and one
 * the reachability suite cannot see because the *route* was always fine.
 *
 * `main.jsx` opens every task the same way — `openTask(k) { setState({ sel: k, taskMode: 'drawer' }) }`
 * — from the list, the board, a comment, a notification and the palette. This component is what lets
 * ours do the same.
 *
 * ⚠️ **It owns the FETCH, not the caller.** A list holds `Task` rows; the detail needs comments,
 * worklogs, subtasks, links and history, which only `useTaskData` knows how to assemble. Passing a
 * row in and hoping would render a detail missing most of itself.
 *
 * ⚠️ **`full` is not handled here** — it is an address (R-8), and the caller navigates. Rendering a
 * full page inside an overlay would put the same task on screen twice, one on top of the other,
 * which is what `task-route.tsx` records happening when its own mode switch tried it.
 */
export function TaskOverlay({
  taskId,
  mode,
  canEdit,
  canSchedule,
  canLogTime,
  canEditWorklog,
  onModeChange,
  onClose,
  onChanged,
  onOpenTask,
}: {
  /** `null` closes it. */
  readonly taskId: string | null;
  readonly mode: TaskMode;
  readonly canEdit: boolean;
  /** `task.schedule` — dependencies. A separate action from `task.update`; see the handlers. */
  readonly canSchedule: boolean;
  readonly canLogTime: boolean;
  readonly canEditWorklog: boolean;
  readonly onModeChange: (next: TaskMode) => void;
  readonly onClose: () => void;
  /** So the list underneath can re-read a row this overlay just changed. */
  readonly onChanged: () => void;
  /** Following a subtask or a link from inside the overlay. */
  readonly onOpenTask: (id: string) => void;
}): ReactNode {
  /**
   * ⚠️⚠️ **BOTH HOOKS RUN UNCONDITIONALLY, WITH A NULL ID WHEN NOTHING IS OPEN.** Calling them
   * inside `if (taskId === null) return null` would make the hook order depend on whether a drawer
   * is open, which React reports as a different error on every open and close. `useTaskData` already
   * takes `string | null` for exactly this.
   */
  const { data, reload } = useTaskData(taskId);
  const searchTasks = useTaskSearch(taskId ?? undefined);
  const writes = useTaskWrites(taskId, data?.task.version, async () => {
    await reload();
    /* ⚠️ The list underneath holds its own copy of this row. Without this, editing a status in the
     * drawer leaves the row behind it showing the old one until something else refetches. */
    onChanged();
  });

  if (taskId === null || data === null) return null;

  return (
    <TaskSurface
      mode={mode}
      open
      data={data}
      onModeChange={onModeChange}
      onClose={onClose}
      handlers={{
        canEdit,
        /**
         * ⚠⚠️ **A SUBTASK OR A LINK SWAPS THE OVERLAY'S TASK RATHER THAN NAVIGATING.** The detail
         * body renders both as buttons, and following one from inside a drawer should stay in the
         * drawer — that is what the frame is for. `onOpen` is the caller's, so the list decides.
         */
        onOpenTask: (task) => {
          onOpenTask(task.id);
        },
        error: writes.error,
        canLogTime,
        canEditWorklog,
        canSchedule,
        onPatch: writes.patch,
        /**
         * ⚠️⚠️ **THE DRAWER AND THE MODAL HAD NO UPLOAD, AND THE PAGE DID — 15 Sep.** This object
         * is hand-written and the page's is too, so a handler added to one reaches the other only
         * if somebody remembers. `onAttachFiles` was added to `task-route.tsx` and not here, which
         * is why attaching worked on `/tasks/HSO-1` and did nothing in the drawer over the list —
         * the same screen, the same component, two chromes and one missing line.
         */
        onAttachFiles: writes.attachFiles,
        onAttachToDescription: writes.attachToDescription,
        onRemoveDescriptionFile: writes.removeDescriptionFile,
        onAddComment: writes.comment,
        /* ⚠️ Item 73 · only offered on your own comment — `task-detail.tsx` says why. */
        onEditComment: (comment, html, mentions) => {
          writes.editComment(comment, html, mentions);
        },
        onDeleteComment: (comment) => {
          writes.deleteComment(comment);
        },
        onLogTime: writes.logTime,
        onDeleteWorklog: writes.deleteWorklog,
        onCreateSubtask: writes.createSubtask,
        onDetachSubtask: writes.detachSubtask,
        onAddLink: writes.addLink,
        onRemoveLink: writes.removeLink,
        onAddDependency: writes.addDependency,
        onRemoveDependency: writes.removeDependency,
        onSearchTasks: searchTasks,
      }}
    />
  );
}
